import React, {useEffect, useState} from 'react';
import {Form, Col, Row, Container} from 'react-bootstrap';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form/dist/react-hook-form.ie11';
import {CircularProgress, Tooltip} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import {withStyles} from '@material-ui/core/styles';
import StyledRadioInput from '../../../Helpers/Inputs/StyledRadioInput';
import NotesPagesNotification from '../../../../icons/NotesPagesNotification.svg';
import TitleComponent from '../../../Common/TitleComponent';
import BackNextButton from '../../../Common/BackNextButton';
import {object, string, array as yuparray, mixed} from 'yup';
import getESignerInfo from './Helpers/getESignData';
import reloadSigners from "./Helpers/reloadSigners";
import {customValidators} from '../../../Helpers/Validators/customValidators';
import ESignerForm from "./SubComponents/ESignerForm";

export default function Esign(props) {

  const {register, errors, handleSubmit, setValue, triggerValidation} = useForm({
    validationSchema: Esign.ValidationSchema(),
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  });

  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const getSigners = async () => {
    const signers = await getESignerInfo(props.fullFormState);
    await props.onFieldChange('esigner', signers);
  }

  const refreshSigner = async () => {
    const onSuccess = async (updatedSigners) => {
      await setIsRefresh(true);
      await setLoading(false);
      // update signers
      await props.onFieldChange('esigner', updatedSigners);
    }

    const onFailure = async () => {
      await setErrorMessage(true);
      await setLoading(false);
    }

    // reset window to top of screen
    window.scrollTo(0, 0);

    await setLoading(true);
    await setErrorMessage(false);

    // get client data
    await reloadSigners(props.fullFormState, onSuccess, onFailure);
  }

  const handleValueChange = async (field, value, onChange) => {
    if (onChange)
      await onChange(field, value);
    else
      await props.onFieldChange(field, value);

    await setValue(field, value);
    await triggerValidation(field);
  }

  useEffect(() => {
    if(!props.formState.esign){
      setIsNextDisabled(false)
    }
    getSigners();
  }, [props.formState.esign])

  useEffect(() => {
    getSigners();
  }, []) 

  useEffect(() => {
    register({name: 'esign'});
    setValue('esign', props.formState.esign);

    props.formState.esigner.forEach((policy, index) => {
      register({name: `esigner[${index}].firstName`});
      setValue(`esigner[${index}].firstName`, policy.firstName);

      register({name: `esigner[${index}].lastName`});
      setValue(`esigner[${index}].lastName`, policy.lastName);

      register({name: `esigner[${index}].phone`});
      setValue(`esigner[${index}].phone`, policy.phone);

      register({name: `esigner[${index}].email`});
      setValue(`esigner[${index}].email`, policy.email);

      register({name: `esigner[${index}].authentication`});
      setValue(`esigner[${index}].authentication`, policy.authentication);
    });
  }, [register, props.formState.esign]);

  const handleOnSubmit = () => {
    if (isNextDisabled) {
      return;
    }

    props.onNext();
  }

  return (
    <Container fluid className="esignComponent border-class whiteBackground">
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <Row className="justify-content-center pt-4 client-coverage">
          <Col lg={4}>
            <TitleComponent icon={NotesPagesNotification} title="Electronic Signature"/>
            {
              loading ?
                <Row className="d-flex justify-content-center">
                  <CircularProgress className="mt-5 mb-5"/>
                </Row> :
                <div>
                  <Form.Group className="mb-0">
                    <div className="d-flex">
                      <p className='esign-title'>Does your client want to E-sign the application?</p>
                      <span>
                          <Tooltip
                            className="mt-0 ml-2"
                            title="E-Sign or Electronic Signature is the process of signing your documents electronically, rather than manually on paper. All parties will receive a secure link via email to start the process. After your identity has been verified, you will be able to review your documents online and sign them electronically. Your location and time of signature will be recorded for verification purposes."
                          >
                              <LightTooltip>
                                  <InfoIcon color="action"></InfoIcon>
                              </LightTooltip>
                          </Tooltip>
                      </span>
                    </div>
                    <StyledRadioInput
                      name="esign"
                      id="esign"
                      label=""
                      value={props.formState.esign}
                      options={[
                        {value: true, label: 'Yes'},
                        {value: false, label: 'No'}
                      ]}
                      onChange={e => handleValueChange('esign', e)}
                      errors={errors.esign}
                      labelCenter={true}
                      inputRef={register}
                    />
                  </Form.Group>
                  {props.formState.esign === true && props.formState.esigner.map((value, index) =>
                    <ESignerForm
                      register={register}
                      errors={errors.esigner && errors.esigner[index]}
                      index={index}
                      key={index}
                      formState={props.formState}
                      setValue={setValue}
                      handleValueChange={handleValueChange}
                      reloadEsigner={refreshSigner}
                      trigger={triggerValidation}
                      setIsNextDisabled={(value) => setIsNextDisabled(value)}
                      isRefresh={isRefresh}
                      onChange={(newForm) => {
                        props.onFieldChange(
                          'esigner',
                          props.formState.esigner.map((form, formIndex) => {
                            return formIndex === index ? newForm : form;
                          })
                        );
                      }}
                    />
                  )}
                  {errorMessage &&
                    <div className='alert alert-danger'>
                      Unable to refresh the information. Please try again after some time.
                    </div>
                  }
                </div>
            }
          </Col>
        </Row>
        <BackNextButton onBack={props.onBack} disabled={isNextDisabled}/>
      </Form>
    </Container>
  );
}

Esign.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

Esign.ValidationSchema = () => {
  return object().shape({
    esign: string().required('This field is required.'),
    esigner: mixed().when('esign', {
      is: (esign) => esign === 'true',
      then: yuparray(object().shape({
          firstName: customValidators.string().isValidNameLimitedSpecialChar('This field is required.', true, false),
          lastName: customValidators.string().isValidNameLimitedSpecialChar('This field is required.', true, false),
          email: string().nullable().email('Email address must be valid.').required('This field is required.'),
          phone: string().nullable().required('This field is required.'),
          authentication: string().nullable().required('This field is required.'),
        })
      ),
      otherwise: mixed().notRequired(),
    }),
  });
}

Esign.DefaultFormState = (prefill, advisorInfo) => {
  return {
    esign: prefill ? true : false,
    ownerEmailList: [],
    ownerPhoneList: [],
    esigner: []
  };
};

const LightTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11
  }
}))(Tooltip);


// WEBPACK FOOTER //
// ./src/components/Product/SharedComponents/ESign/ESign.js